import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/client/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/client/components/ui/alert-dialog";
import { Button } from "@/client/components/ui/button";
import {
  ExternalLink,
  Trash2,
  Image as ImageIcon,
  User,
  MapPin,
  Loader2,
} from "lucide-react";
import { useTabActions } from "@/client/hooks/use-tabs-context";
import type { CodexType } from "@/client/hooks/use-codex";
import type { ImageWithCodexInfo } from "@/client/hooks/use-all-images";
import { apiJson } from "@/client/lib/api";
import { reportMutationError } from "@/client/db";

interface ImagePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  image: ImageWithCodexInfo | null;
  onDeleted: () => void;
}

// Format bytes to human-readable size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Get category display info
function getCategoryInfo(category: string) {
  switch (category) {
    case "portrait":
      return { label: "Portrait", icon: User, color: "bg-blue-500/10 text-blue-500" };
    case "background":
      return { label: "Background", icon: MapPin, color: "bg-green-500/10 text-green-500" };
    case "sprite":
      return { label: "Sprite", icon: ImageIcon, color: "bg-purple-500/10 text-purple-500" };
    default:
      return { label: category, icon: ImageIcon, color: "bg-muted text-muted-foreground" };
  }
}

// Get codex type badge color
function getCodexTypeColor(type: string | null) {
  switch (type) {
    case "character":
      return "bg-blue-500/10 text-blue-500";
    case "location":
      return "bg-green-500/10 text-green-500";
    case "object":
      return "bg-amber-500/10 text-amber-500";
    case "event":
      return "bg-red-500/10 text-red-500";
    case "lore":
      return "bg-purple-500/10 text-purple-500";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function ImagePreviewDialog({
  open,
  onOpenChange,
  image,
  onDeleted,
}: ImagePreviewDialogProps) {
  const { openCodexTab } = useTabActions();
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  }, []);

  useEffect(() => {
    if (!open || !image) setDimensions(null);
  }, [open, image]);

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setShowDeleteConfirm(false);
    }
    onOpenChange(newOpen);
  };

  const handleDelete = async () => {
    if (!image) return;

    setIsDeleting(true);
    try {
      await apiJson(`/api/images/${image.id}`, {
        method: "DELETE",
      });
      setShowDeleteConfirm(false);
      onOpenChange(false);
      onDeleted();
    } catch (error) {
      reportMutationError("image", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenInNewTab = () => {
    if (image) {
      window.open(`/api/images/${image.id}`, "_blank");
    }
  };

  const handleOpenCodexEntry = () => {
    if (image?.codexId && image.codexName && image.codexType) {
      openCodexTab(image.codexId, image.codexName, image.codexType as CodexType);
      onOpenChange(false);
    }
  };

  if (!image) return null;

  const categoryInfo = getCategoryInfo(image.category);
  const CategoryIcon = categoryInfo.icon;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {image.codexName || image.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Preview and details for {image.codexName || image.name}
            </DialogDescription>
          </DialogHeader>

          {/* Image Preview */}
          <div className="relative bg-muted/50 rounded-lg overflow-hidden flex items-center justify-center min-h-[200px]">
            <img
              src={`/api/images/${image.id}`}
              alt={image.codexName || image.name}
              className="max-h-[50vh] w-auto max-w-full object-contain rounded"
              onLoad={handleImageLoad}
            />
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {/* Resolution */}
            <div>
              <span className="text-muted-foreground">Resolution</span>
              <p className="font-medium">
                {dimensions
                  ? `${dimensions.width} × ${dimensions.height}`
                  : "Loading..."}
              </p>
            </div>

            {/* File Size */}
            <div>
              <span className="text-muted-foreground">File Size</span>
              <p className="font-medium">
                {image.size !== null ? formatFileSize(image.size) : "Unknown"}
              </p>
            </div>

            {/* Category */}
            <div>
              <span className="text-muted-foreground">Category</span>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${categoryInfo.color}`}
                >
                  <CategoryIcon className="h-3 w-3" />
                  {categoryInfo.label}
                </span>
                {image.isDefault && image.category === "background" && (
                  <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary">
                    Default
                  </span>
                )}
              </div>
            </div>

            {/* Image Name */}
            <div>
              <span className="text-muted-foreground">Name</span>
              <p className="font-medium">{image.name}</p>
            </div>

            {/* Linked Codex Entry */}
            {image.codexName && image.codexId && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Linked To</span>
                <button
                  type="button"
                  className="flex items-center gap-2 mt-0.5 hover:underline cursor-pointer text-left"
                  onClick={handleOpenCodexEntry}
                >
                  <span className="font-medium">{image.codexName}</span>
                  {image.codexType && (
                    <span
                      className={`px-2 py-0.5 rounded-md text-xs font-medium capitalize ${getCodexTypeColor(image.codexType)}`}
                    >
                      {image.codexType}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleOpenInNewTab}
              className="gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open in New Tab
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Image</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{image.codexName || image.name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
